import styles from "./JoinChannelTask.module.scss";
import { useSelector } from "../../../../hooks";
import { PageTitleBlock } from "../../../../components/PageTitleBlock/PageTitleBlock";
import { CardWrapper } from "../../../../components/CardWrapper/CardWrapper";
import { JoinChannelItem } from "./JoinChannelItem";
import { ITask, ITaskReward, JoinChannelTaskCardType, hasRecurringRewards } from "../../../../components/TaskCard/TaskCard";
import { FC, useState } from "react";
import { Selector } from "../../../../components/Selector/Selector";
import { useNavigate } from "../../../../hooks/navigate";
import { AppContext } from "../../../../components/Menu/MenuButton";
import { Button } from "../../../../components/Buttons/Button/Button";
import warningIcon from '../../../../icons/warn2.png';
import { CheckJoinChannelTaskResult } from "../../../../redux/tasks/check-join-channel.handler";
import { getTimeLeft } from "../../../../utils/time";
import { isQuizAllowedToStart } from "../../../../utils/quiz-task";

export enum JoinChannelTaskStatus {
    Disabled = 0,
    Enabled = 1,
    Completed = 2,
    Archive = 3,
    Finished = 4,
}

export enum JoinChannelTaskError {
    BadUserNames = 1,
}

export class JoinChannelTaskDto implements ITask {
    taskId!: number;
    title!: string;
    link!: string;
    description!: string;
    image!: string;
    reward!: ITaskReward;
    status!: JoinChannelTaskStatus;
    subscribed!: boolean;
    result?: CheckJoinChannelTaskResult;
    pendingDate?: number;
    lastRewardDate?: number;
    linkOpened?: boolean;
}

export interface IJoinChannelTasksUserPenalty {
    score: number;
    penalties: {
        pendingTime: number;
        reward: number;
        tasksAmount: number;
    }
}

export class JoinChannelTasksDto {
    activeTasks!: JoinChannelTaskDto[];
    archiveTasks!: JoinChannelTaskDto[];
    error?: JoinChannelTaskError;
    penalty?: IJoinChannelTasksUserPenalty;
}

const INTERESTS_COUNT_TO_QUIZ_OFFER = 3;

export const JoinChannelTaskPage = () => {
    const { interestsCount, joinChannelTasks, quizTask } = useSelector(store => store.tasks);
    const [isEnabled, setIsEnabled] = useState(false);

    const navigate = useNavigate();

    const getType = (task: JoinChannelTaskDto): JoinChannelTaskCardType => {
        if (task.status === JoinChannelTaskStatus.Archive)
            return 'archive';

        return task.subscribed ? 'active' : 'inactive';
    }

    const TasksList: FC<{ list: JoinChannelTaskDto[], type?: JoinChannelTaskCardType }> = ({ list, type }) => {
        return (
            <>
                { list.map((task, index) => (
                    <JoinChannelItem
                        key={ `task${task.taskId}` }
                        task={ task }
                        index={ index }
                        type={ type ?? getType(task) }
                    />
                )) }
            </>
        );
    }

    const getQuizOffer = () => {
        if (!quizTask)
            return null;

        const canStartQuiz = isQuizAllowedToStart(quizTask.allowDate);
        const handler = canStartQuiz ? () => navigate(AppContext.Quiz) : undefined;

        const buttonCaption = canStartQuiz
            ? 'Пройти викторину'
            : `Доступна через ${getTimeLeft(quizTask.allowDate, -1)}`;

        return (
            <span className={styles.quizOffer}>                
                <div>
                    Чтобы получать больше заданий на подписку пройдите
                    { canStartQuiz
                        ? <span className={styles.link} onClick={ handler }> викторину</span>
                        : <> викторину</>
                    }
                </div>
                <Button
                    caption={ buttonCaption }
                    type={ canStartQuiz ? 'green' : 'white' }
                    handler={ handler ?? (() => {}) }
                    disabled={ !canStartQuiz }
                />
            </span>
        );
    }

    const noTasks = () => {
        return (
            <>
                <span className={ styles.noTasks }>Нет доступных задач</span>
                { interestsCount < INTERESTS_COUNT_TO_QUIZ_OFFER && getQuizOffer() }
            </>
        );
    }

    const errorBlock = (error: JoinChannelTaskError) => {
        return (
            <div className={ styles.noTasks }>
                <img className={ styles.warningIcon } src={ warningIcon } alt='warning' />
                <div>В вашем имени присутствуют запрещенные слова (bot и т.д.)</div>
                <div>Пожалуйста, измените его и перезайдите в игру для доступа к заданиям.</div>
            </div>
        );
    }

    const getActiveList = () => {
        const tasksCount = (joinChannelTasks?.activeTasks ?? []).length;

        if (joinChannelTasks?.error) {
            return errorBlock(joinChannelTasks?.error);
        }

        return tasksCount > 0
            ? <TasksList list={ joinChannelTasks?.activeTasks ?? [] } type={ 'new' } />
            : noTasks();
    }

    const archiveTasksWithRecurringRewards = (joinChannelTasks?.archiveTasks ?? [])
        .filter(task => hasRecurringRewards(task.reward));

    const getArchiveList = () => {
        return archiveTasksWithRecurringRewards.length > 0
            ? <TasksList list={ archiveTasksWithRecurringRewards } />
            : <span className={ styles.noTasks }>Нет доступных задач</span>;
    }

    return (
        <div className={styles.tasksWrapper}>
            <PageTitleBlock
                pageTitle="Подписаться на канал"
                pageDescription="Каналы найдем, нажмем подписаться - и все, монетки начнут размножаться"
            />
            <CardWrapper>
                { archiveTasksWithRecurringRewards.length > 0 &&
                    <div className={ styles.selectorContainer }>
                        <Selector label='Новые' rightValue="Ежедневные награды" isEnabled={ isEnabled } setIsEnabled={ setIsEnabled } />
                    </div>
                }

                { isEnabled ? getArchiveList() : getActiveList() }
            </CardWrapper>
        </div>
    );
}